// ============================================================================
// Fleet & Assets rollup (view b) — fleet-admin surface. Summary tiles + two
// tabs (Vehicles / Equipment) over one dataset. Vehicles table: vehicle,
// assignee (EID→name), region, odometer + monthly avg, status, registration
// chip, MTD cost. Equipment: floor machines. Add/Edit opens the asset modals.
// Track reality, surface gaps as chips — never block.
// ============================================================================
import { isFleetAdmin } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, Plus, Zap } from 'lucide-react';
import { PageHeader, EmptyState, ErrorState, Skeleton, TabBar } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { complianceChip, chipClasses, isComplianceGap } from './compliance';
import { statusLabel, statusTone, isCountableMachine, isBroken, isStale, confirmedAgo, STALE_DAYS } from './machine-status';
import {
  useFleetRollup,
  avgMilesPerMonth,
  fmtMoney,
  fmtDate,
  isActiveVehicle,
  machineLocation,
  type VehicleRow,
  type MachineRow,
  type ReadingRow,
  type CostRow,
} from './hooks/use-fleet-data';
import { VehicleFormModal, MachineFormModal } from './asset-modals';
import { vehicleStatusLabel, vehicleStatusTone, isInactiveVehicleStatus } from './vehicle-status';

const OVERDUE_DAYS = 35;

type Tab = 'vehicles' | 'equipment';

export default function FleetRollup() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  // Off by default: a sold van is not part of the fleet you manage today. The
  // hook turns this into a QUERY filter, so the rows are never fetched — see
  // ACTIVE_VEHICLES_PGRST_FILTER.
  const [includeInactive, setIncludeInactive] = useState(false);
  const { vehicles, machines, readings, mtdCosts, nameMap, loading, error, refetch } =
    useFleetRollup(includeInactive);
  const [tab, setTab] = useState<Tab>('vehicles');
  const [vehModal, setVehModal] = useState<{ open: boolean; row: VehicleRow | null }>({ open: false, row: null });
  const [macModal, setMacModal] = useState<{ open: boolean; row: MachineRow | null }>({ open: false, row: null });

  // readings grouped by vehicle (all readings, desc from hook)
  const readingsByVehicle = useMemo(() => {
    const m = new Map<string, ReadingRow[]>();
    for (const r of readings) {
      const list = m.get(r.vehicle_id) ?? [];
      list.push(r);
      m.set(r.vehicle_id, list);
    }
    return m;
  }, [readings]);

  const mtdByVehicle = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of mtdCosts as CostRow[]) {
      if (!c.vehicle_id) continue;
      m.set(c.vehicle_id, (m.get(c.vehicle_id) ?? 0) + Number(c.amount));
    }
    return m;
  }, [mtdCosts]);

  const mtdByMachine = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of mtdCosts as CostRow[]) {
      if (!c.machine_id) continue;
      m.set(c.machine_id, (m.get(c.machine_id) ?? 0) + Number(c.amount));
    }
    return m;
  }, [mtdCosts]);

  const summary = useMemo(() => {
    const activeVeh = vehicles.filter(isActiveVehicle);
    const activeMac = machines.filter((m) => isCountableMachine(m.status));
    let gaps = 0;
    let overdue = 0;
    for (const v of activeVeh) {
      const levels = [v.registration_exp, v.insurance_exp, v.emissions_exp]
        .map((d) => complianceChip(d).level)
        // emissions "missing" is n/a (null allowed) — only count reg+ins missing + any expired/amber
        .filter((lvl, i) => !(i === 2 && lvl === 'missing'));
      if (levels.some(isComplianceGap)) gaps++;
      const latest = readingsByVehicle.get(v.vehicle_id)?.[0]?.read_on ?? v.last_mileage_at ?? null;
      const days = latest ? (Date.now() - new Date(latest).getTime()) / 86_400_000 : Infinity;
      if (days > OVERDUE_DAYS) overdue++;
    }
    // Broken and stale are the two numbers the National Accounts Manager
    // actually opens this page for.
    // Stale reads 26 of 26 today, which is the honest state of the fleet.
    const broken = activeMac.filter((m) => isBroken(m.status)).length;
    const staleMac = activeMac.filter((m) => isStale(m.last_confirmed_at)).length;
    return {
      activeVeh: activeVeh.length,
      activeMac: activeMac.length,
      gaps,
      overdue,
      broken,
      staleMac,
    };
  }, [vehicles, machines, readingsByVehicle]);

  if (!isFleetAdmin(profile)) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <EmptyState title="Fleet & Assets" body="This view is available to leadership, national AMs, and finance. Your assigned vehicle appears on your Home screen." />
      </div>
    );
  }
  if (error) return <ErrorState body={error} onRetry={refetch} />;

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 md:p-6">
      <PageHeader
        eyebrow="Fleet & Assets"
        title="Fleet & Assets"
        meta={`${summary.activeVeh} active vehicles · ${summary.activeMac} floor machines`}
        actions={
          <button
            type="button"
            onClick={() => (tab === 'vehicles' ? setVehModal({ open: true, row: null }) : setMacModal({ open: true, row: null }))}
            className="inline-flex items-center gap-1.5 rounded-md bg-tcs-inverse px-3 py-1.5 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse/90"
          >
            <Plus className="h-4 w-4" /> {tab === 'vehicles' ? 'Add vehicle' : 'Add equipment'}
          </button>
        }
      />

      {/* summary tiles — vehicles first, then the two equipment numbers the
          National Accounts Manager opens this page for. Broken is danger, not
          amber: it is the one that means somebody is driving somewhere. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Tile label="Active vehicles" value={summary.activeVeh} />
        <Tile label="Floor machines" value={summary.activeMac} />
        <Tile label="Broken" value={summary.broken} tone={summary.broken > 0 ? 'danger' : 'muted'} />
        <Tile label={`Unconfirmed >${STALE_DAYS}d`} value={summary.staleMac} tone={summary.staleMac > 0 ? 'amber' : 'muted'} />
        <Tile label="Compliance gaps" value={summary.gaps} tone={summary.gaps > 0 ? 'amber' : 'muted'} />
        <Tile label="Mileage overdue" value={summary.overdue} tone={summary.overdue > 0 ? 'amber' : 'muted'} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <TabBar
          tabs={[
            { id: 'vehicles', label: `Vehicles (${vehicles.length})` },
            { id: 'equipment', label: `Equipment (${machines.length})` },
          ]}
          activeId={tab}
          onChange={(id) => setTab(id as Tab)}
        />
        {/* Only meaningful on the Vehicles tab. Same switch idiom as My
            Accounts' "Show closed" — role=switch pill, off by default, the
            title says which query is running. */}
        {tab === 'vehicles' && (
          <ShowInactiveToggle value={includeInactive} onChange={setIncludeInactive} />
        )}
      </div>

      {loading ? (
        <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
      ) : tab === 'vehicles' ? (
        vehicles.length === 0 ? (
          includeInactive ? (
            <EmptyState title="No vehicles yet" body="Add the first vehicle to start tracking the fleet." />
          ) : (
            // Distinguishing these two matters: "no active vehicles" and "no
            // vehicles at all" look identical once the list is filtered, and
            // the wrong one sends somebody hunting for a bug.
            <EmptyState title="No active vehicles" body="Every vehicle on record is sold or retired. Turn on “Show sold & retired” to see them." />
          )
        ) : (
          <div className="overflow-x-auto rounded-lg border border-tcs-border bg-tcs-raised">
            <table className="min-w-full divide-y divide-tcs-border text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                  <th className="px-3 py-2">Vehicle</th>
                  <th className="px-3 py-2">Assignee</th>
                  <th className="px-3 py-2">Region</th>
                  <th className="px-3 py-2 text-right">Odometer</th>
                  <th className="px-3 py-2 text-right">Avg mi/mo</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Registration</th>
                  <th className="px-3 py-2 text-right">MTD cost</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-tcs-border">
                {vehicles.map((v) => {
                  const chip = complianceChip(v.registration_exp);
                  const avg = avgMilesPerMonth(readingsByVehicle.get(v.vehicle_id) ?? []);
                  const odo = readingsByVehicle.get(v.vehicle_id)?.[0]?.odometer ?? v.mileage ?? null;
                  // A row only ever appears here when the toggle is on. It gets
                  // a left edge stripe as well as the status badge — the same
                  // never-colour-alone rule the broken-machine row follows, and
                  // the stripe survives a narrow screen where the status column
                  // has scrolled out of view.
                  const inactive = isInactiveVehicleStatus(v.status);
                  return (
                    <tr key={v.vehicle_id} onClick={() => navigate(`/fleet/vehicle/${v.vehicle_id}`)} className={`cursor-pointer hover:bg-tcs-surface ${inactive ? 'text-tcs-text-muted' : ''}`}>
                      <td className={`px-3 py-2 ${inactive ? 'border-l-[3px] border-l-tcs-border-strong' : ''}`}>
                        <div className="flex items-center gap-1.5 font-medium text-tcs-text">
                          {v.powertrain === 'ev' ? <Zap className="h-3.5 w-3.5 text-st-ok-fg" /> : <Car className="h-3.5 w-3.5 text-tcs-text-muted" />}
                          {v.make_model ?? v.vehicle_id}
                        </div>
                        <div className="text-xs text-tcs-text-muted"><span className="font-mono">{v.license_plate ?? v.vehicle_id}</span></div>
                      </td>
                      <td className="px-3 py-2 text-tcs-text-secondary">{v.assigned_employee_eid ? nameMap.get(v.assigned_employee_eid) ?? v.assigned_employee_eid : '—'}</td>
                      <td className="px-3 py-2 text-tcs-text-secondary">{v.location ?? '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{odo != null ? odo.toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{avg != null ? avg.toLocaleString() : '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${chipClasses(vehicleStatusTone(v.status))}`}>{vehicleStatusLabel(v.status)}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${chipClasses(chip.level)}`}>{chip.label}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{mtdByVehicle.has(v.vehicle_id) ? fmtMoney(mtdByVehicle.get(v.vehicle_id)!) : '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setVehModal({ open: true, row: v }); }} className="rounded border border-tcs-border bg-tcs-raised px-2 py-1 text-xs text-tcs-text hover:bg-tcs-surface">Edit</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : machines.length === 0 ? (
        <EmptyState title="No equipment yet" body="Add the first floor machine to start tracking equipment." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-tcs-border bg-tcs-raised">
          <table className="min-w-full divide-y divide-tcs-border text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2">Machine</th>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Last maint.</th>
                <th className="px-3 py-2">Next maint.</th>
                <th className="px-3 py-2">Warranty</th>
                <th className="px-3 py-2 text-right">MTD cost</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-tcs-border">
              {machines.map((m) => {
                const warr = complianceChip(m.warranty_exp);
                const broken = isBroken(m.status);
                const stale = isStale(m.last_confirmed_at);
                const where = machineLocation(m);
                return (
                  <tr
                    key={m.machine_id}
                    onClick={() => navigate(`/fleet/equipment/${m.machine_id}`)}
                    className="cursor-pointer hover:bg-tcs-surface"
                  >
                    {/* Broken is a left edge stripe AND a chip — never colour
                        alone, and the stripe survives a narrow screen where the
                        status column has scrolled out of view. */}
                    <td className={`px-3 py-2 ${broken ? 'border-l-[3px] border-l-st-danger-solid' : ''}`}>
                      {/* NO WRENCH HERE. It rendered on every machine
                          regardless of status, so it distinguished nothing —
                          and the one thing a wrench says is "needs repair",
                          which is already carried by the left edge stripe and
                          the status chip. It survives only where it is an
                          ACTION affordance: the Report broken and Set status
                          buttons in machine-actions.tsx. */}
                      <div className="font-medium text-tcs-text">{[m.machine_brand, m.machine_model].filter(Boolean).join(' ') || m.machine_id}</div>
                      <div className="text-xs text-tcs-text-muted font-mono">{m.machine_id}{m.serial_number ? ` · ${m.serial_number}` : ''}</div>
                    </td>
                    <td className="px-3 py-2 text-tcs-text-secondary">
                      {/* location_display from v_floor_machines. The OF-/FAC-
                          id is a tooltip, never the value on the row. */}
                      {where.kind === 'unassigned' ? (
                        <span className="text-tcs-text-muted">Unassigned</span>
                      ) : (
                        <span title={where.id ?? undefined}>{where.display}</span>
                      )}
                      <div className="text-xs text-tcs-text-muted">
                        confirmed {confirmedAgo(m.last_confirmed_at)}
                        {stale && (
                          <span className="ml-1.5 inline-flex rounded-full border border-st-warn-border bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-medium text-st-warn-fg-strong">
                            stale
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${chipClasses(statusTone(m.status))}`}>{statusLabel(m.status)}</span>
                    </td>
                    <td className="px-3 py-2 text-tcs-text-secondary">{fmtDate(m.last_maintenance_date)}</td>
                    <td className="px-3 py-2 text-tcs-text-secondary">{fmtDate(m.next_maintenance_due)}</td>
                    <td className="px-3 py-2">
                      {m.warranty_exp ? <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${chipClasses(warr.level)}`}>{warr.label}</span> : <span className="text-tcs-text-muted">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{mtdByMachine.has(m.machine_id) ? fmtMoney(mtdByMachine.get(m.machine_id)!) : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" onClick={(e) => { e.stopPropagation(); setMacModal({ open: true, row: m }); }} className="rounded border border-tcs-border bg-tcs-raised px-2 py-1 text-xs text-tcs-text hover:bg-tcs-surface">Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {vehModal.open && (
        <VehicleFormModal
          vehicle={vehModal.row}
          onClose={() => setVehModal({ open: false, row: null })}
          onSaved={(saved) => {
            // On the ADD path, go straight to the new vehicle's card. The id
            // was minted by the database a moment ago and the person who typed
            // the form has never seen it — the toast names it, and this puts
            // them on the page that shows it. On the EDIT path, stay put.
            if (!vehModal.row) navigate(`/fleet/vehicle/${saved.vehicle_id}`);
            else refetch();
          }}
        />
      )}
      {macModal.open && (
        <MachineFormModal machine={macModal.row} onClose={() => setMacModal({ open: false, row: null })} onSaved={refetch} />
      )}
    </div>
  );
}

/** Mirrors My Accounts' ShowClosedToggle — the app's existing switch idiom for
 *  "let me see the ones we normally hide". Off is the operational default. */
function ShowInactiveToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={`inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium transition-colors ${
        value
          ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
          : 'border-tcs-border-strong bg-tcs-bg text-tcs-text-secondary hover:bg-tcs-surface'
      }`}
      title={
        value
          ? 'Showing sold and retired vehicles'
          : 'Sold and retired vehicles hidden (they are not fetched at all)'
      }
    >
      <span
        aria-hidden="true"
        className={`inline-block h-1.5 w-1.5 rounded-full ${value ? 'bg-tcs-raised' : 'bg-tcs-text-muted'}`}
      />
      Show sold &amp; retired
    </button>
  );
}

function Tile({ label, value, tone = 'muted' }: { label: string; value: number; tone?: 'muted' | 'amber' | 'danger' }) {
  const numTone =
    value > 0 && tone === 'danger'
      ? 'text-st-danger-fg'
      : value > 0 && tone === 'amber'
        ? 'text-st-warn-fg'
        : 'text-tcs-text';
  const borderTone =
    value > 0 && tone === 'danger'
      ? 'border-st-danger-border'
      : value > 0 && tone === 'amber'
        ? 'border-st-warn-border'
        : 'border-tcs-border';
  return (
    <div className={`rounded-xl border bg-tcs-bg p-3 ${borderTone}`}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">{label}</div>
      <div className={`mt-1 font-mono text-2xl font-semibold ${numTone}`}>{value}</div>
    </div>
  );
}
